import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, ChevronLeft, ChevronRight, Clock, Copy, Loader2, MessageCircle } from 'lucide-react';
import TalentPeek from '@/components/find-talent/TalentPeek';
import { ftApi } from '@/components/find-talent/api';
import { KatiaChat } from '@/components/find-talent/KatiaChat';
import { BriefSummary } from '@/components/find-talent/BriefSummary';
import type { FTSession, SessionListItem, TranscriptEntry } from '@/components/find-talent/types';

// V1 flow (docs/creator-project-flow-v1.md): intake → review/confirm
// ("Post to Hey Joe") → pending → approved (share link) / rejected
// (edit-and-resubmit). Matching/shortlist/tapes are later phases — MatchGrid
// and TapeViewer stay on disk, this page just doesn't render them.

const isDevHost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname.startsWith('dev-') || window.location.hostname === 'localhost');

const openChatlio = () => {
  if (window._chatlio && typeof window._chatlio.show === 'function') {
    try {
      window._chatlio.show({ expanded: true });
      return;
    } catch {
      /* fall through to mailto */
    }
  }
  window.open('mailto:hello@heyjoe.io', '_blank');
};

const FindTalentPage: React.FC = () => {
  // Live as of 2026-08-17. The moderation loop is wired end to end: casting's
  // create + status endpoints are deployed and authenticating, the share link is
  // withheld until an admin approves, the self-approve route is behind an
  // explicit env flag that prod does not set, and sweepFindTalentModeration
  // delivers the decision even if the creator never reopens the page.
  //
  // isDevHost is still used below to keep the mock-approve control off prod.

  const [session, setSession] = useState<FTSession | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  // Every project (session) the user has — drafts and posted alike.
  const [projects, setProjects] = useState<SessionListItem[]>([]);
  const [switching, setSwitching] = useState(false);
  // List view: which row is expanded, and full session details per row
  // (fetched lazily on first expand).
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, FTSession>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const applySession = useCallback((s: FTSession) => {
    setSession(s);
    setTranscript(s.transcript || []);
  }, []);

  const refreshProjects = useCallback(async () => {
    try {
      const { sessions } = await ftApi.listSessions();
      setProjects(sessions);
    } catch {
      /* strip is non-critical */
    }
  }, []);

  // Mount: resume the latest session, or start a fresh one.
  useEffect(() => {
    (async () => {
      try {
        const { session: existing } = await ftApi.loadSession();
        if (existing) {
          applySession(existing);
        } else {
          const fresh = await ftApi.start();
          applySession(fresh);
        }
        refreshProjects();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load Find Talent.');
      } finally {
        setLoading(false);
      }
    })();
  }, [applySession, refreshProjects]);

  // Switch to another project from the strip.
  const handleSwitchProject = async (id: string) => {
    if (!id || id === session?.session_id || switching) return;
    setSwitching(true);
    setError(null);
    try {
      const { session: s } = await ftApi.loadSessionById(id);
      applySession(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open that project.');
    } finally {
      setSwitching(false);
    }
  };

  // Start a brand-new project (fresh Katia session).
  const handleNewProject = async () => {
    if (switching) return;
    setSwitching(true);
    setError(null);
    try {
      const fresh = await ftApi.start();
      applySession(fresh);
      refreshProjects();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start a new project.');
    } finally {
      setSwitching(false);
    }
  };

  const projectStatusLabel = (p: SessionListItem) => {
    if (p.casting_project_id) {
      if (p.moderation_status === 'approved') return 'Live';
      if (p.moderation_status === 'rejected') return 'Needs changes';
      return 'In review';
    }
    return p.is_complete ? 'Ready to post' : 'Draft';
  };

  // Expand a row: lazily fetch the full session for its details.
  const handleToggleExpand = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!details[id]) {
      try {
        const { session: s } = await ftApi.loadSessionById(id);
        setDetails((prev) => ({ ...prev, [id]: s }));
      } catch {
        /* row shows without details */
      }
    }
  };

  const handleCopyProjectLink = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { /* url is visible to select */ }
  };

  // Poll while the project is pending — the poll is how approval/rejection
  // reaches the page (no webhooks in V1).
  useEffect(() => {
    const sessionId = session?.session_id;
    const active = !!session?.casting_project_id && session?.moderation_status === 'pending';
    if (!active || !sessionId) return;

    const tick = async () => {
      try {
        const st = await ftApi.status(sessionId);
        setSession((prev) =>
          prev
            ? {
                ...prev,
                moderation_status: st.moderation_status,
                moderation_note: st.moderation_note,
                public_url: st.public_url ?? prev.public_url,
              }
            : prev
        );
      } catch {
        /* transient — keep polling */
      }
    };
    pollRef.current = setInterval(tick, 30000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [session?.session_id, session?.casting_project_id, session?.moderation_status]);

  const handleSend = async (text: string) => {
    if (!session) return;
    setSending(true);
    setTranscript((t) => [...t, { role: 'user', text }]);
    try {
      const res = await ftApi.message(session.session_id, text);
      setTranscript((t) => [...t, { role: 'katia', text: res.message }]);
      setSession((prev) =>
        prev ? { ...prev, brief: res.brief, roles: res.roles, is_complete: res.is_complete } : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Message failed.');
    } finally {
      setSending(false);
    }
  };

  // Upload a PDF casting brief — the server parses it into the structured
  // state and Katia carries on with whatever's still missing.
  const handleUploadBrief = async (file: File) => {
    if (!session) return;
    setSending(true);
    setTranscript((t) => [...t, { role: 'user', text: `(Uploaded brief: ${file.name})` }]);
    try {
      const res = await ftApi.uploadBrief(session.session_id, file);
      setTranscript((t) => [...t, { role: 'katia', text: res.message }]);
      setSession((prev) =>
        prev ? { ...prev, brief: res.brief, roles: res.roles, is_complete: res.is_complete } : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that brief.');
    } finally {
      setSending(false);
    }
  };

  // Direct fill from the brief panel's inline inputs (chat stays primary).
  const handleSetField = async (key: string, value: string) => {
    if (!session) return;
    try {
      const res = await ftApi.setField(session.session_id, key, value);
      setSession((prev) =>
        prev ? { ...prev, brief: res.brief, roles: res.roles, is_complete: res.is_complete } : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that field.');
    }
  };

  // The stop: post the finished brief to Hey Joe for review.
  const handlePost = async () => {
    if (!session) return;
    setSubmitting(true);
    try {
      const res = await ftApi.createProject(session.session_id);
      setSession((prev) =>
        prev
          ? {
              ...prev,
              casting_project_id: res.casting_project_id,
              moderation_status: (res.moderation_status as FTSession['moderation_status']) || 'pending',
              public_url: res.public_url ?? prev.public_url,
            }
          : prev
      );
      refreshProjects();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit your project for review.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!session?.public_url) return;
    try {
      await navigator.clipboard.writeText(session.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the URL is visible to select manually */
    }
  };

  // DEV-ONLY control to walk the moderation gate before casting is wired.
  const handleDevApprove = async () => {
    if (!session) return;
    try {
      await ftApi.devApprove(session.session_id);
      setSession((prev) => (prev ? { ...prev, moderation_status: 'approved' } : prev));
      refreshProjects();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed.');
    }
  };

  const shell = (children: React.ReactNode) => (
    <main
      style={{
        background:
          'radial-gradient(120% 60% at 15% 0%, oklch(0.26 0.045 75 / 0.55) 0%, transparent 55%), var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 1120, padding: '0 24px 96px' }}>
        <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
          <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
            <ChevronLeft size={12} strokeWidth={2} /> Home
          </Link>
          <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
          <span style={{ color: 'var(--hj-ink)' }}>Find talent</span>
        </div>
        <header style={{ padding: '10px 0 28px', borderBottom: '1px solid var(--hj-hairline-soft)', marginBottom: 28 }}>
          <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-yellow-ink)', fontSize: 12, letterSpacing: '0.12em' }}>Find talent</div>
          <h1
            className="font-hj-display m-0"
            style={{ fontSize: 'clamp(36px, 4.6vw, 56px)', lineHeight: 1, color: 'var(--hj-ink)', letterSpacing: '-0.025em' }}
          >
            Find talent.
          </h1>
          <p className="m-0" style={{ marginTop: 12, color: 'var(--hj-ink)', fontSize: 15.5, maxWidth: 620, lineHeight: 1.5 }}>
            Katia builds your casting brief with you, then posts your project on Hey Joe. Once it&apos;s approved you get a
            shareable casting page — submissions land right in your casting studio.
          </p>
        </header>
        {/* Projects list — every intake (draft or posted). Rows expand to show
            the brief details, roles, and (once live) the share link. */}
        {projects.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
              <div className="mono-label" style={{ color: 'var(--hj-ink-2)' }}>Your projects</div>
              <button
                type="button"
                onClick={handleNewProject}
                disabled={switching}
                className="inline-flex items-center gap-1.5"
                style={{ padding: '8px 14px', borderRadius: 999, background: 'var(--hj-yellow)', border: 'none', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: switching ? 0.6 : 1 }}
              >
                {switching ? <Loader2 size={13} className="animate-spin" /> : '+'} New project
              </button>
            </div>
            <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
              {projects.map((p, i) => {
                const active = p.session_id === session?.session_id;
                const label = projectStatusLabel(p);
                const expanded = expandedId === p.session_id;
                const d = details[p.session_id];
                return (
                  <div key={p.session_id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--hj-hairline-soft)' }}>
                    <button
                      type="button"
                      onClick={() => handleToggleExpand(p.session_id)}
                      className="w-full flex items-center gap-3 text-left"
                      style={{ padding: '13px 16px', background: 'transparent', border: 0, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      {expanded
                        ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-ink-3)' }} />
                        : <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-ink-3)' }} />}
                      <span className="flex-1 truncate" style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--hj-ink)' }}>
                        {p.title}
                        {active && <span className="mono-label" style={{ marginLeft: 8, fontSize: 10, color: 'var(--hj-ink-3)' }}>open</span>}
                      </span>
                      <span
                        className="mono-label flex-shrink-0"
                        style={{
                          padding: '3px 10px', borderRadius: 999, fontSize: 10.5,
                          background: label === 'Live' ? 'var(--hj-yellow)' : 'var(--hj-surface-2)',
                          color: label === 'Live' ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)',
                          border: label === 'Live' ? 'none' : '1px solid var(--hj-hairline)',
                        }}
                      >
                        {label}
                      </span>
                    </button>
                    {expanded && (
                      <div style={{ padding: '0 16px 14px 35px' }}>
                        {!d ? (
                          <div className="flex items-center gap-2" style={{ color: 'var(--hj-ink-3)', fontSize: 13 }}>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                          </div>
                        ) : (
                          <>
                            {d.brief && (
                              <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5, maxWidth: 640 }}>
                                {[d.brief.production_type, d.brief.geography, d.brief.dates, d.brief.rate].filter(Boolean).join(' · ')}
                              </p>
                            )}
                            {d.brief?.description && (
                              <p className="m-0" style={{ marginTop: 6, fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.5, maxWidth: 640 }}>
                                {d.brief.description.length > 220 ? d.brief.description.slice(0, 220) + '…' : d.brief.description}
                              </p>
                            )}
                            {(d.roles?.length ?? 0) > 0 && (
                              <p className="m-0" style={{ marginTop: 6, fontSize: 13, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
                                <span className="mono-label" style={{ fontSize: 10.5, color: 'var(--hj-ink-3)', marginRight: 6 }}>Roles</span>
                                {d.roles.map((r) => r.title).join(', ')}
                              </p>
                            )}
                            {d.moderation_status === 'rejected' && d.moderation_note && (
                              <p className="m-0" style={{ marginTop: 8, fontSize: 13, color: 'var(--hj-ink)' }}>Note from review: {d.moderation_note}</p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 12 }}>
                              {d.public_url && d.moderation_status === 'approved' && (
                                <>
                                  <span className="truncate" style={{ maxWidth: 320, fontSize: 12.5, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>{d.public_url}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleCopyProjectLink(p.session_id, d.public_url!)}
                                    className="inline-flex items-center gap-1.5 mono-label"
                                    style={{ padding: '7px 14px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 11 }}
                                  >
                                    {copiedId === p.session_id ? <Check size={12} /> : <Copy size={12} />} {copiedId === p.session_id ? 'Copied' : 'Copy link'}
                                  </button>
                                </>
                              )}
                              {!active && (
                                <button
                                  type="button"
                                  onClick={() => handleSwitchProject(p.session_id)}
                                  className="inline-flex items-center gap-1.5 mono-label"
                                  style={{ padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontWeight: 600, cursor: 'pointer', fontSize: 11 }}
                                >
                                  {label === 'Draft' || label === 'Ready to post' ? 'Continue with Katia' : 'Open'} <ArrowRight size={12} />
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {error && (
          <div
            style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 'var(--hj-r-md)', background: 'oklch(0.28 0.09 25 / 0.25)', border: '1px solid oklch(0.5 0.12 25 / 0.4)', color: 'var(--hj-ink)', fontSize: 13.5 }}
          >
            {error}
          </div>
        )}
        {children}
      </div>
    </main>
  );

  if (loading) {
    return shell(
      <div className="flex flex-col items-center justify-center" style={{ padding: '80px 0' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
        <p className="m-0" style={{ marginTop: 12, fontSize: 13, color: 'var(--hj-ink-3)' }}>Loading…</p>
      </div>
    );
  }

  const mod = session?.moderation_status;
  const projectCreated = !!session?.casting_project_id;

  // ---- Pending review (the stop) ----
  if (projectCreated && mod === 'pending') {
    return shell(
      <div
        className="text-center mx-auto"
        style={{ maxWidth: 760, padding: '48px 28px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}
      >
        <div className="inline-flex items-center justify-center rounded-full" style={{ width: 52, height: 52, background: 'var(--hj-surface-2)', marginBottom: 16 }}>
          <Clock className="w-6 h-6" style={{ color: 'var(--hj-yellow-ink)' }} />
        </div>
        <div className="mono-label" style={{ color: 'var(--hj-yellow-ink)', letterSpacing: '0.12em', marginBottom: 10 }}>In review</div>
        <h2 className="font-hj-display m-0" style={{ fontSize: 24, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
          Your project is being reviewed.
        </h2>
        <p className="m-0" style={{ marginTop: 10, fontSize: 14.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
          Usually within 24 hours. We&apos;ll email you the moment it&apos;s approved — you&apos;ll get a public casting page
          you can share anywhere.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap" style={{ marginTop: 22 }}>
          <button
            type="button"
            onClick={openChatlio}
            className="inline-flex items-center gap-1.5 mono-label"
            style={{ padding: '10px 18px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', border: 'none', fontWeight: 700, cursor: 'pointer' }}
          >
            <MessageCircle size={14} /> Chat with us
          </button>
          <a href="mailto:hello@heyjoe.io" className="inline-flex items-center gap-1.5 mono-label no-underline" style={{ padding: '10px 18px', borderRadius: 999, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontWeight: 600 }}>
            Email us if you&apos;re in a hurry
          </a>
        </div>
        {isDevHost() && String(session?.casting_project_id || '').startsWith('mock-') && (
          <div style={{ marginTop: 24 }}>
            <button type="button" onClick={handleDevApprove} className="mono-label" style={{ background: 'none', border: '1px dashed var(--hj-hairline)', borderRadius: 999, padding: '6px 14px', color: 'var(--hj-ink-4)', cursor: 'pointer' }}>
              Dev: approve now
            </button>
          </div>
        )}
        <TalentPeek />
      </div>
    );
  }

  // ---- Rejected: edit and resubmit ----
  if (projectCreated && mod === 'rejected') {
    return shell(
      <div className="text-center mx-auto" style={{ maxWidth: 520, padding: '48px 28px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
        <h2 className="font-hj-display m-0" style={{ fontSize: 24, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
          A few changes needed.
        </h2>
        <p className="m-0" style={{ marginTop: 10, fontSize: 14.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
          {session?.moderation_note || 'Your project needs a few edits before it can go out. Adjust the details and resubmit.'}
        </p>
        <div style={{ marginTop: 22 }}>
          <button
            type="button"
            onClick={() => setSession((prev) => (prev ? { ...prev, casting_project_id: null, moderation_status: null } : prev))}
            className="inline-flex items-center gap-1.5 mono-label"
            style={{ padding: '10px 18px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', border: 'none', fontWeight: 700, cursor: 'pointer' }}
          >
            Edit &amp; resubmit <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  // ---- Approved: the share link is the payoff ----
  if (projectCreated && mod === 'approved') {
    return shell(
      <div className="text-center mx-auto" style={{ maxWidth: 560, padding: '48px 28px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
        <div className="inline-flex items-center justify-center rounded-full" style={{ width: 52, height: 52, background: 'var(--hj-surface-2)', marginBottom: 16 }}>
          <Check className="w-6 h-6" style={{ color: 'var(--hj-yellow-ink)' }} />
        </div>
        <div className="mono-label" style={{ color: 'var(--hj-yellow-ink)', letterSpacing: '0.12em', marginBottom: 10 }}>Approved</div>
        <h2 className="font-hj-display m-0" style={{ fontSize: 24, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
          {session?.brief?.title || 'Your project'} is live.
        </h2>
        <p className="m-0" style={{ marginTop: 10, fontSize: 14.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
          Your public casting page is up. Share the link anywhere — socials, email, group chats — and submissions land
          in your casting studio.
        </p>
        {session?.public_url ? (
          <div
            className="flex items-center gap-2"
            style={{ marginTop: 22, padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}
          >
            <span
              className="flex-1 truncate text-left"
              style={{ fontSize: 13.5, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}
            >
              {session.public_url}
            </span>
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 mono-label flex-shrink-0"
              style={{ padding: '8px 14px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', border: 'none', fontWeight: 700, cursor: 'pointer' }}
            >
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        ) : (
          <p className="m-0" style={{ marginTop: 18, fontSize: 13, color: 'var(--hj-ink-2)' }}>
            Your share link is on its way — check back in a moment or watch your email.
          </p>
        )}
      </div>
    );
  }

  // ---- Intake: two-column chat + live brief, then review & confirm ----
  return shell(
    <>
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
        <KatiaChat transcript={transcript} sending={sending} locked={false} onSend={handleSend} onUploadBrief={handleUploadBrief} />
        <div>
          <BriefSummary brief={session?.brief || null} roles={session?.roles || []} onSetField={handleSetField} />
          {session?.is_complete && (
            <div
              style={{ marginTop: 14, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}
            >
              <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
                Happy with the brief? Post it for review — or keep chatting with Katia to change anything first.
              </p>
              <button
                type="button"
                onClick={handlePost}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 mono-label"
                style={{ marginTop: 12, padding: '13px 26px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', border: 'none', fontWeight: 700, fontSize: 14, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1 }}
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                Post to Hey Joe
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FindTalentPage;
